import React, { useCallback, useEffect, useState } from 'react'
import {
    Alert,
    App as AntdApp,
    Button,
    Descriptions,
    Modal,
    Progress,
    Space,
    Spin,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
} from 'antd'
import type { TableColumnsType } from 'antd'
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    FileSearchOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import PageLayout from '../../components/layout/PageLayout/PageLayout'
import { PAGINATED_LIST_TABLE_SIZE } from '../../components/common/PaginatedListTable'
import {
    downloadModelEvaluationReport,
    getModelEvaluationTask,
    getModelEvaluationV3Report,
} from '../../services/modelManagementService'
import type { ModelEvaluationTaskDetail } from '../../types/modelManagement'
import type {
    ModelEvaluationDecisionCheck,
    ModelEvaluationMetric,
    ModelEvaluationV3Report,
    ModelEvaluationWeightedScore,
    ValidationMatchOutcome,
    ValidationSchemeSide,
} from '../../types/modelValidation'
import { getModelManagementErrorDetail } from './modelManagement/pageConfig'
import ModelEvaluationComparison from './modelValidation/ModelEvaluationComparison'
import { EvaluationTaskConfigurationSummary } from './modelValidation/ModelEvaluationTaskConfiguration'
import { formatBasisPoints } from './modelValidation/modelValidationUtils'
import ValidationRuleEditor from './modelValidation/ValidationRuleEditor'
import './ModelEvaluationReportPage.css'

const recommendationColors: Record<string, string> = {
    manual_review: 'warning',
    not_recommended: 'error',
    recommended: 'success',
}

interface MetricDrilldown {
    categoryKey: string
    count: number
    outcome: ValidationMatchOutcome
    schemeSide: ValidationSchemeSide
}

type WeightedScoreMetricKey = Exclude<
    keyof ModelEvaluationWeightedScore,
    'scheme_side'
>

type MetricExplanationKey =
    | 'truthTotal'
    | 'correct'
    | 'misclassified'
    | 'missed'
    | 'pureFalsePositive'
    | 'recognitionRate'
    | 'errorRate'
    | 'missRate'
    | 'precision'
    | 'recall'
    | 'f1'

const metricExplanationLabelKeys: Record<MetricExplanationKey, string> = {
    truthTotal: 'admin:modelValidation.report.metricColumns.truthTotal',
    correct: 'admin:modelValidation.report.metricColumns.correct',
    misclassified: 'admin:modelValidation.columns.misclassified',
    missed: 'admin:modelValidation.report.metricColumns.missed',
    pureFalsePositive:
        'admin:modelValidation.report.metricColumns.pureFalsePositive',
    recognitionRate:
        'admin:modelValidation.report.metricColumns.recognitionRate',
    errorRate: 'admin:modelValidation.report.metricColumns.errorRate',
    missRate: 'admin:modelValidation.report.metricColumns.missRate',
    precision: 'admin:modelValidation.report.metricColumns.precision',
    recall: 'admin:modelValidation.report.metricColumns.recall',
    f1: 'admin:modelValidation.report.metricColumns.f1',
}

interface WeightedScoreComparisonRow {
    baseline: number | null
    candidate: number | null
    change: number | null
    labelKey: string
    metricKey: WeightedScoreMetricKey
}

const weightedScoreMetrics: Array<{
    labelKey: string
    metricKey: WeightedScoreMetricKey
}> = [
    {
        labelKey:
            'admin:modelValidation.report.metricColumns.weightedRecognitionRate',
        metricKey: 'recognition_rate_bp',
    },
    {
        labelKey:
            'admin:modelValidation.report.metricColumns.weightedErrorRate',
        metricKey: 'error_rate_bp',
    },
    {
        labelKey:
            'admin:modelValidation.report.metricColumns.weightedMissRate',
        metricKey: 'miss_rate_bp',
    },
    {
        labelKey: 'admin:modelValidation.report.metricColumns.compositeScore',
        metricKey: 'composite_score_bp',
    },
]

const getDisplayedMetricCount = (
    metric: ModelEvaluationMetric,
    outcome: ValidationMatchOutcome,
): number => {
    if (outcome === 'false_positive') {
        return (
            metric.false_positive_count ??
            Math.max(metric.fp - metric.misclassified, 0)
        )
    }
    if (outcome === 'false_negative') {
        return (
            metric.false_negative_count ??
            Math.max(metric.fn - metric.misclassified, 0)
        )
    }
    if (outcome === 'misclassified') return metric.misclassified
    return metric.tp
}

const orderMetrics = (
    metrics: ModelEvaluationMetric[],
): ModelEvaluationMetric[] =>
    (['baseline', 'candidate'] as const).flatMap((schemeSide) => {
        const schemeMetrics = metrics.filter(
            (metric) => metric.scheme_side === schemeSide,
        )
        return [
            ...schemeMetrics
                .filter((metric) => metric.category_key !== '__overall__')
                .sort((left, right) =>
                    left.category_key.localeCompare(right.category_key),
                ),
            ...schemeMetrics.filter(
                (metric) => metric.category_key === '__overall__',
            ),
        ]
    })
const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    window.URL.revokeObjectURL(url)
}

interface ModelEvaluationReportContentProps {
    embedded?: boolean
    onBack?: () => void
    taskId: number
}

interface MetricColumnTitleProps {
    ariaLabel: string
    label: string
    tooltip: string
}

const MetricColumnTitle: React.FC<MetricColumnTitleProps> = ({
    ariaLabel,
    label,
    tooltip,
}) => (
    <span className="model-evaluation-report__metric-title">
        {label}
        <Tooltip title={tooltip} trigger={['hover', 'focus']}>
            <button
                aria-label={ariaLabel}
                className="model-evaluation-report__metric-help"
                type="button"
            >
                <InfoCircleOutlined aria-hidden="true" />
            </button>
        </Tooltip>
    </span>
)

export const ModelEvaluationReportContent: React.FC<
    ModelEvaluationReportContentProps
> = ({ embedded = false, onBack, taskId }) => {
    const { message } = AntdApp.useApp()
    const { t } = useTranslation(['admin', 'navigation'])
    const parsedTaskId = taskId
    const [task, setTask] = useState<ModelEvaluationTaskDetail | null>(null)
    const [report, setReport] = useState<ModelEvaluationV3Report | null>(null)
    const [activeTab, setActiveTab] = useState('decision')
    const [loading, setLoading] = useState(true)
    const [exporting, setExporting] = useState(false)
    const [ruleModalOpen, setRuleModalOpen] = useState(false)
    const [metricDrilldown, setMetricDrilldown] =
        useState<MetricDrilldown | null>(null)

    const loadReport = useCallback(
        async (silent = false) => {
            if (!Number.isInteger(parsedTaskId)) return
            if (!silent) setLoading(true)
            try {
                const taskData = await getModelEvaluationTask(parsedTaskId)
                setTask(taskData)
                if (taskData.status === 'succeeded') {
                    setReport(await getModelEvaluationV3Report(parsedTaskId))
                }
            } catch (error) {
                if (!silent) {
                    message.error(
                        getModelManagementErrorDetail(
                            error,
                            t(
                                'admin:modelValidation.messages.loadReportFailed',
                            ),
                            t,
                        ),
                    )
                }
            } finally {
                if (!silent) setLoading(false)
            }
        },
        [message, parsedTaskId, t],
    )

    const handleExport = async () => {
        setExporting(true)
        try {
            downloadBlob(
                await downloadModelEvaluationReport(parsedTaskId, 'xlsx'),
                `model-evaluation-v3-${parsedTaskId}.xlsx`,
            )
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t('admin:modelValidation.messages.operationFailed'),
                    t,
                ),
            )
        } finally {
            setExporting(false)
        }
    }

    useEffect(() => {
        void loadReport()
    }, [loadReport])

    useEffect(() => {
        if (!task || !['queued', 'running'].includes(task.status)) {
            return undefined
        }
        const timer = window.setInterval(() => void loadReport(true), 5000)
        return () => window.clearInterval(timer)
    }, [loadReport, task])

    const orderedMetrics = orderMetrics(report?.metrics || [])
    const getCategoryLabel = (categoryKey: string) =>
        categoryKey === '__overall__'
            ? t('admin:modelValidation.rules.overall')
            : t(
                  `admin:modelValidation.rules.categoryWeightFields.${categoryKey}`,
                  categoryKey,
              )
    const renderMetricCount = (
        metric: ModelEvaluationMetric,
        outcome: ValidationMatchOutcome,
        columnLabel: string,
    ) => {
        const count = getDisplayedMetricCount(metric, outcome)
        if (count === 0) return 0
        const schemeLabel = t(
            `admin:modelValidation.scheme.${metric.scheme_side}`,
        )
        const categoryLabel = getCategoryLabel(metric.category_key)
        return (
            <Button
                aria-label={`${schemeLabel} ${categoryLabel} ${columnLabel} ${count}`}
                className="model-evaluation-report__metric-link"
                onClick={() =>
                    setMetricDrilldown({
                        categoryKey: metric.category_key,
                        count,
                        outcome,
                        schemeSide: metric.scheme_side,
                    })
                }
                size="small"
                type="link"
            >
                {count}
            </Button>
        )
    }
    const renderMetricTitle = (metricKey: MetricExplanationKey) => {
        const label = t(metricExplanationLabelKeys[metricKey])
        return (
            <MetricColumnTitle
                ariaLabel={t(
                    'admin:modelValidation.report.viewMetricExplanation',
                    { metric: label },
                )}
                label={label}
                tooltip={t(
                    `admin:modelValidation.report.metricExplanation.${metricKey}`,
                )}
            />
        )
    }
    const metricColumns: TableColumnsType<ModelEvaluationMetric> = [
        {
            title: t('admin:modelValidation.columns.scheme'),
            dataIndex: 'scheme_side',
            width: 112,
            render: (value: string) =>
                t(`admin:modelValidation.scheme.${value}`),
        },
        {
            title: t('admin:modelValidation.columns.category'),
            dataIndex: 'category_key',
            width: 120,
            render: getCategoryLabel,
        },
        {
            title: renderMetricTitle('truthTotal'),
            dataIndex: 'truth_total',
            align: 'right',
            width: 104,
        },
        {
            title: renderMetricTitle('correct'),
            dataIndex: 'tp',
            align: 'right',
            render: (_value, metric) =>
                renderMetricCount(
                    metric,
                    'true_positive',
                    t('admin:modelValidation.report.metricColumns.correct'),
                ),
            width: 112,
        },
        {
            title: renderMetricTitle('misclassified'),
            dataIndex: 'misclassified',
            align: 'right',
            render: (_value, metric) =>
                renderMetricCount(
                    metric,
                    'misclassified',
                    t('admin:modelValidation.columns.misclassified'),
                ),
            width: 104,
        },
        {
            title: renderMetricTitle('missed'),
            dataIndex: 'false_negative_count',
            align: 'right',
            render: (_value, metric) =>
                renderMetricCount(
                    metric,
                    'false_negative',
                    t('admin:modelValidation.report.metricColumns.missed'),
                ),
            width: 112,
        },
        {
            title: renderMetricTitle('pureFalsePositive'),
            dataIndex: 'false_positive_count',
            align: 'right',
            render: (_value, metric) =>
                renderMetricCount(
                    metric,
                    'false_positive',
                    t(
                        'admin:modelValidation.report.metricColumns.pureFalsePositive',
                    ),
                ),
            width: 112,
        },
        {
            title: renderMetricTitle('recognitionRate'),
            dataIndex: 'recognition_rate_bp',
            align: 'right',
            render: formatBasisPoints,
            width: 112,
        },
        {
            title: renderMetricTitle('errorRate'),
            dataIndex: 'error_rate_bp',
            align: 'right',
            render: formatBasisPoints,
            width: 112,
        },
        {
            title: renderMetricTitle('missRate'),
            dataIndex: 'miss_rate_bp',
            align: 'right',
            render: formatBasisPoints,
            width: 112,
        },
        {
            title: t(
                'admin:modelValidation.report.metricColumns.compositeScore',
            ),
            dataIndex: 'composite_score_bp',
            align: 'right',
            render: formatBasisPoints,
            width: 112,
        },
    ]

    const technicalMetricColumns: TableColumnsType<ModelEvaluationMetric> = [
        metricColumns[0],
        metricColumns[1],
        {
            title: renderMetricTitle('precision'),
            dataIndex: 'precision_bp',
            align: 'right',
            render: formatBasisPoints,
        },
        {
            title: renderMetricTitle('recall'),
            dataIndex: 'recall_bp',
            align: 'right',
            render: formatBasisPoints,
        },
        {
            title: renderMetricTitle('f1'),
            dataIndex: 'f1_bp',
            align: 'right',
            render: formatBasisPoints,
        },
    ]

    const formatPercentageChange = (value: number | null): string => {
        if (value === null) return '-'
        return `${value > 0 ? '+' : ''}${formatBasisPoints(value)}`
    }

    const weightedScoreColumns: TableColumnsType<WeightedScoreComparisonRow> = [
        {
            title: t('admin:modelValidation.report.metricName'),
            dataIndex: 'labelKey',
            render: (labelKey: string) => t(labelKey),
        },
        {
            title: t('admin:modelValidation.scheme.baseline'),
            dataIndex: 'baseline',
            align: 'right',
            render: (value: number | null) =>
                value === null ? '-' : formatBasisPoints(value),
        },
        {
            title: t('admin:modelValidation.scheme.candidate'),
            dataIndex: 'candidate',
            align: 'right',
            render: (value: number | null) =>
                value === null ? '-' : formatBasisPoints(value),
        },
        {
            title: t('admin:modelValidation.report.change'),
            dataIndex: 'change',
            align: 'right',
            render: formatPercentageChange,
        },
    ]

    const decisionCheckColumns: TableColumnsType<ModelEvaluationDecisionCheck> = [
        {
            title: t('admin:modelValidation.report.decisionCheck'),
            dataIndex: 'metric',
            render: (metric: string) =>
                t(`admin:modelValidation.report.metric.${metric}`),
        },
        {
            title: t('admin:modelValidation.report.actualValue'),
            dataIndex: 'actual',
            align: 'right',
            render: formatBasisPoints,
        },
        {
            title: t('admin:modelValidation.report.requiredValue'),
            key: 'required',
            align: 'right',
            render: (_, check) =>
                `${check.comparison === 'minimum' ? '≥' : '≤'} ${formatBasisPoints(check.target)}`,
        },
        {
            title: t('admin:modelValidation.report.checkResult'),
            dataIndex: 'passed',
            align: 'center',
            render: (passed: boolean) => (
                <Tag color={passed ? 'success' : 'error'}>
                    {t(
                        `admin:modelValidation.report.${passed ? 'passed' : 'failed'}`,
                    )}
                </Tag>
            ),
        },
    ]

    if (loading) {
        return embedded ? (
            <div
                style={{
                    display: 'grid',
                    minHeight: 240,
                    placeItems: 'center',
                }}
            >
                <Spin />
            </div>
        ) : (
            <Spin fullscreen />
        )
    }

    const coveragePercent = task?.requested_sample_count
        ? Math.round(
              ((task.processed_sample_count || 0) /
                  task.requested_sample_count) *
                  100,
          )
        : 0

    const weightedScoreBySide = new Map(
        report?.weighted_scores.map((score) => [score.scheme_side, score]) || [],
    )
    const weightedScoreRows: WeightedScoreComparisonRow[] = report?.weighted_scores
        .length
        ? weightedScoreMetrics.map(({ labelKey, metricKey }) => {
              const baseline =
                  weightedScoreBySide.get('baseline')?.[metricKey] ?? null
              const candidate =
                  weightedScoreBySide.get('candidate')?.[metricKey] ?? null
              return {
                  baseline,
                  candidate,
                  change:
                      baseline === null || candidate === null
                          ? null
                          : candidate - baseline,
                  labelKey,
                  metricKey,
              }
          })
        : []

    const content = (
        <div className="model-evaluation-report">
            {task ? (
                <section
                    aria-labelledby="evaluation-report-configuration"
                    className="model-evaluation-report__configuration"
                >
                    <div className="model-evaluation-report__configuration-header">
                        <div>
                            <Typography.Text type="secondary">
                                {task.name}
                            </Typography.Text>
                            <Typography.Title
                                id="evaluation-report-configuration"
                                level={5}
                            >
                                {t(
                                    'admin:modelValidation.columns.configuration',
                                )}
                            </Typography.Title>
                        </div>
                        <Space
                            className="model-evaluation-report__actions"
                            wrap
                        >
                            {onBack ? (
                                <Button
                                    icon={<ArrowLeftOutlined />}
                                    onClick={onBack}
                                >
                                    {t(
                                        'admin:modelValidation.actions.backToTasks',
                                    )}
                                </Button>
                            ) : null}
                            {report?.rule ? (
                                <Button
                                    aria-label={t(
                                        'admin:modelValidation.actions.viewRule',
                                    )}
                                    icon={<FileSearchOutlined />}
                                    onClick={() => setRuleModalOpen(true)}
                                >
                                    {t(
                                        'admin:modelValidation.actions.viewRule',
                                    )}
                                </Button>
                            ) : null}
                            {report ? (
                                <Button
                                    icon={<DownloadOutlined />}
                                    loading={exporting}
                                    onClick={() => void handleExport()}
                                    type="primary"
                                >
                                    {t(
                                        'admin:modelValidation.actions.exportExcel',
                                    )}
                                </Button>
                            ) : null}
                        </Space>
                    </div>
                    <EvaluationTaskConfigurationSummary
                        task={task}
                        variant="report"
                    />
                </section>
            ) : null}
            {!report ? (
                <Alert
                    className="model-evaluation-report__status-alert"
                    description={t(
                        'admin:modelValidation.report.runningDescription',
                    )}
                    message={t('admin:modelValidation.report.runningTitle')}
                    showIcon
                    type={task?.status === 'failed' ? 'error' : 'info'}
                />
            ) : null}
            <Tabs
                activeKey={activeTab}
                className="model-evaluation-report__tabs"
                onChange={setActiveTab}
                items={[
                    {
                        key: 'decision',
                        label: t('admin:modelValidation.report.decisionTab'),
                        children: report ? (
                            <div className="model-evaluation-report__decision">
                                <section
                                    aria-label={t(
                                        'admin:modelValidation.report.decisionTab',
                                    )}
                                    className="model-evaluation-report__release-decision"
                                >
                                    <div className="model-evaluation-report__decision-band">
                                        <div className="model-evaluation-report__decision-result">
                                            <Typography.Text type="secondary">
                                                {t(
                                                    'admin:modelValidation.report.conclusion',
                                                )}
                                            </Typography.Text>
                                            <Tag
                                                color={
                                                    recommendationColors[
                                                        report.recommendation
                                                    ]
                                                }
                                            >
                                                {t(
                                                    `admin:modelValidation.recommendation.${report.recommendation}`,
                                                )}
                                            </Tag>
                                        </div>
                                        <div className="model-evaluation-report__coverage">
                                            <Typography.Text type="secondary">
                                                {t(
                                                    'admin:modelValidation.columns.coverage',
                                                )}
                                            </Typography.Text>
                                            <Progress
                                                percent={coveragePercent}
                                                format={() =>
                                                    `${report.processed_sample_count}/${report.requested_sample_count}`
                                                }
                                                status={
                                                    report.failed_sample_count
                                                        ? 'exception'
                                                        : 'normal'
                                                }
                                            />
                                        </div>
                                        <div className="model-evaluation-report__stat">
                                            <Typography.Text type="secondary">
                                                {t(
                                                    'admin:modelValidation.columns.processedSamples',
                                                )}
                                            </Typography.Text>
                                            <Typography.Text strong>
                                                {report.processed_sample_count}
                                            </Typography.Text>
                                        </div>
                                        <div className="model-evaluation-report__stat">
                                            <Typography.Text type="secondary">
                                                {t(
                                                    'admin:modelValidation.columns.failedSamples',
                                                )}
                                            </Typography.Text>
                                            <Typography.Text
                                                strong
                                                type={
                                                    report.failed_sample_count
                                                        ? 'danger'
                                                        : undefined
                                                }
                                            >
                                                {report.failed_sample_count}
                                            </Typography.Text>
                                        </div>
                                    </div>
                                    {!report.evaluation_valid ? (
                                        <Alert
                                            description={t(
                                                'admin:modelValidation.report.invalidEvaluationDescription',
                                            )}
                                            message={t(
                                                'admin:modelValidation.report.invalidEvaluationTitle',
                                            )}
                                            showIcon
                                            type="error"
                                        />
                                    ) : null}
                                    {report.decision_checks.length > 0 ? (
                                        <Table
                                            bordered
                                            columns={decisionCheckColumns}
                                            dataSource={report.decision_checks}
                                            pagination={false}
                                            rowKey="metric"
                                            scroll={{ x: 640 }}
                                            size={PAGINATED_LIST_TABLE_SIZE}
                                        />
                                    ) : null}
                                </section>
                                {weightedScoreRows.length > 0 ? (
                                    <section
                                        aria-label={t(
                                            'admin:modelValidation.report.weightedSummary',
                                        )}
                                        className="model-evaluation-report__metrics"
                                    >
                                        <Typography.Title level={5}>
                                            {t(
                                                'admin:modelValidation.report.weightedSummary',
                                            )}
                                        </Typography.Title>
                                        <Table
                                            bordered
                                            columns={weightedScoreColumns}
                                            dataSource={weightedScoreRows}
                                            pagination={false}
                                            rowKey="metricKey"
                                            scroll={{ x: 560 }}
                                            size={PAGINATED_LIST_TABLE_SIZE}
                                        />
                                    </section>
                                ) : null}
                                <section
                                    aria-label={t(
                                        'admin:modelValidation.report.performanceMetrics',
                                    )}
                                    className="model-evaluation-report__metrics"
                                >
                                    <Typography.Title level={5}>
                                        {t(
                                            'admin:modelValidation.report.performanceMetrics',
                                        )}
                                    </Typography.Title>
                                    <Table
                                        bordered
                                        columns={metricColumns}
                                        dataSource={orderedMetrics}
                                        pagination={false}
                                        rowKey={(record) =>
                                            `${record.scheme_side}:${record.category_key}`
                                        }
                                        scroll={{ x: 1232 }}
                                        size={PAGINATED_LIST_TABLE_SIZE}
                                    />
                                </section>
                                <section
                                    aria-label={t(
                                        'admin:modelValidation.report.technicalMetrics',
                                    )}
                                    className="model-evaluation-report__technical-metrics"
                                >
                                    <Typography.Title level={5}>
                                        {t(
                                            'admin:modelValidation.report.technicalMetrics',
                                        )}
                                    </Typography.Title>
                                    <Typography.Text type="secondary">
                                        {t(
                                            'admin:modelValidation.report.technicalMetricHint',
                                        )}
                                    </Typography.Text>
                                    <Table
                                        columns={technicalMetricColumns}
                                        dataSource={orderedMetrics}
                                        pagination={false}
                                        rowKey={(record) =>
                                            `technical:${record.scheme_side}:${record.category_key}`
                                        }
                                        scroll={{ x: 660 }}
                                        size={PAGINATED_LIST_TABLE_SIZE}
                                    />
                                </section>
                            </div>
                        ) : (
                            <Progress percent={coveragePercent} />
                        ),
                    },
                    {
                        key: 'comparison',
                        label: t('admin:modelValidation.report.comparisonTab'),
                        children: (
                            <ModelEvaluationComparison taskId={parsedTaskId} />
                        ),
                    },
                ]}
            />
            <Modal
                destroyOnClose
                footer={null}
                maskClosable={false}
                onCancel={() => setMetricDrilldown(null)}
                open={Boolean(metricDrilldown)}
                title={t('admin:modelValidation.report.metricDrilldownTitle')}
                width="min(1440px, 96vw)"
            >
                {metricDrilldown ? (
                    <div className="model-evaluation-report__metric-detail">
                        <Typography.Text type="secondary">
                            {t(
                                'admin:modelValidation.report.metricDrilldownSummary',
                                {
                                    category: getCategoryLabel(
                                        metricDrilldown.categoryKey,
                                    ),
                                    count: metricDrilldown.count,
                                    outcome: t(
                                        `admin:modelValidation.outcome.${metricDrilldown.outcome}`,
                                    ),
                                    scheme: t(
                                        `admin:modelValidation.scheme.${metricDrilldown.schemeSide}`,
                                    ),
                                },
                            )}
                        </Typography.Text>
                        <ModelEvaluationComparison
                            focus={{
                                categoryKey:
                                    metricDrilldown.categoryKey ===
                                    '__overall__'
                                        ? undefined
                                        : metricDrilldown.categoryKey,
                                outcome: metricDrilldown.outcome,
                                schemeSide: metricDrilldown.schemeSide,
                            }}
                            taskId={parsedTaskId}
                        />
                    </div>
                ) : null}
            </Modal>
            <Modal
                footer={null}
                maskClosable={false}
                onCancel={() => setRuleModalOpen(false)}
                open={ruleModalOpen}
                title={t('admin:modelValidation.rules.details')}
                width={880}
            >
                {report?.rule ? (
                    <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: '100%' }}
                    >
                        <Descriptions bordered column={1} size="small">
                            <Descriptions.Item
                                label={t('admin:modelValidation.columns.rule')}
                            >
                                {report.rule.name}
                            </Descriptions.Item>
                        </Descriptions>
                        <div
                            style={{
                                maxHeight: 'calc(100vh - 260px)',
                                overflowY: 'auto',
                            }}
                        >
                            <ValidationRuleEditor
                                disabled
                                onChange={() => undefined}
                                value={report.rule.snapshot}
                            />
                        </div>
                    </Space>
                ) : null}
            </Modal>
        </div>
    )

    if (embedded) return content

    return (
        <PageLayout
            title={task?.name || t('admin:modelValidation.report.title')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.modelManagement') },
                {
                    title: t('admin:modelValidation.evaluations.title'),
                    path: '/admin/model-management/evaluations',
                },
                { title: task?.name || `#${taskId}` },
            ]}
        >
            {content}
        </PageLayout>
    )
}

const ModelEvaluationReportPage: React.FC = () => {
    const navigate = useNavigate()
    const { taskId } = useParams<{ taskId: string }>()
    const parsedTaskId = Number(taskId)

    if (!Number.isInteger(parsedTaskId)) return null

    return (
        <ModelEvaluationReportContent
            onBack={() => navigate('/admin/model-management/evaluations')}
            taskId={parsedTaskId}
        />
    )
}

export default ModelEvaluationReportPage
